import { useQuery as useReactQuery } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import LogoIcon from "../components/LogoIcon";
import Seo from "../components/Seo";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Field, FieldGroup } from "../components/ui/field";
import { authClient, LOGIN_PATH, WORKSPACE_PATH } from "../lib/authClient";
import { isAllowedCrossOriginRedirect } from "../lib/chatHost";

function GoogleIcon() {
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 18 18"
			xmlns="http://www.w3.org/2000/svg"
		>
			<title>Google</title>
			<path
				d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
				fill="#4285F4"
			/>
			<path
				d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
				fill="#34A853"
			/>
			<path
				d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
				fill="#FBBC05"
			/>
			<path
				d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 2.58Z"
				fill="#EA4335"
			/>
		</svg>
	);
}

function MicrosoftIcon() {
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 21 21"
			xmlns="http://www.w3.org/2000/svg"
		>
			<title>Microsoft</title>
			<rect x="1" y="1" width="9" height="9" fill="#F25022" />
			<rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
			<rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
			<rect x="11" y="11" width="9" height="9" fill="#FFB900" />
		</svg>
	);
}

const BRAND_BG = `
	radial-gradient(ellipse at 75% 20%, oklch(0.42 0.13 58 / 0.22) 0%, transparent 50%),
	radial-gradient(ellipse at 15% 85%, oklch(0.36 0.10 50 / 0.16) 0%, transparent 50%),
	radial-gradient(ellipse at 50% 50%, oklch(0.30 0.06 100 / 0.06) 0%, transparent 60%),
	linear-gradient(170deg, oklch(0.25 0.04 160), oklch(0.19 0.03 150))
`;

function CustomSignIn() {
	const session = authClient.useSession();
	const [searchParams] = useSearchParams();

	const [error, setError] = useState("");
	const [loading, setLoading] = useState<"google" | "microsoft" | null>(null);

	// `redirect` is either a same-origin path or an allowed chat-subdomain URL;
	// anything else ignored to prevent open-redirect.
	const redirectParam = searchParams.get("redirect") ?? "";
	const isCrossOriginRedirect =
		redirectParam.length > 0 && isAllowedCrossOriginRedirect(redirectParam);
	// Relative path only ("/invite/…"), never "//host" — keeps it same-origin.
	const isSameOriginRedirect =
		redirectParam.startsWith("/") && !redirectParam.startsWith("//");
	const postLoginPath = isSameOriginRedirect ? redirectParam : WORKSPACE_PATH;
	const callbackURL = isCrossOriginRedirect
		? redirectParam
		: `${window.location.origin}${postLoginPath}`;

	// Mint a Better Auth one-time-token so the chat host can claim the session:
	// crossDomain plugin stores it per-origin in localStorage (no shared cookie),
	// so without an OTT it redirect-loops. Chat host auto-exchanges `?ott=` via /cross-domain/one-time-token/verify.
	const ottQuery = useReactQuery({
		queryKey: ["chat-cross-origin-ott", session.data?.session?.token],
		queryFn: async () => {
			const result = await authClient.oneTimeToken.generate();
			return result.data?.token ?? null;
		},
		enabled:
			!session.isPending && Boolean(session.data) && isCrossOriginRedirect,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: Number.POSITIVE_INFINITY,
	});

	if (!session.isPending && session.data) {
		if (isCrossOriginRedirect) {
			if (ottQuery.isLoading || ottQuery.fetchStatus === "fetching") {
				return null;
			}
			// OTT mint failed — send to workspace instead of looping back to chat.
			if (!ottQuery.data) {
				return <Navigate to={WORKSPACE_PATH} replace />;
			}
			const url = new URL(redirectParam);
			url.searchParams.set("ott", ottQuery.data);
			window.location.replace(url.toString());
			return null;
		}
		return <Navigate to={postLoginPath} replace />;
	}

	async function handleGoogleSignIn() {
		setError("");
		setLoading("google");
		try {
			await authClient.signIn.social({
				provider: "google",
				callbackURL,
			});
		} catch (err) {
			console.error("Google sign-in error:", err);
			setError("Could not connect to Google. Please try again.");
			setLoading(null);
		}
	}

	async function handleMicrosoftSignIn() {
		setError("");
		setLoading("microsoft");
		try {
			await authClient.signIn.social({
				provider: "microsoft",
				callbackURL,
			});
		} catch (err) {
			console.error("Microsoft sign-in error:", err);
			setError("Could not connect to Microsoft. Please try again.");
			setLoading(null);
		}
	}

	return (
		<div className="grid min-h-screen lg:grid-cols-[1fr_1.2fr]">
			<div className="flex flex-col bg-background">
				<div className="h-1 bg-gradient-to-r from-forest-deep via-forest to-copper/60 lg:hidden" />

				<div className="flex flex-1 flex-col px-6 py-8 sm:px-10 lg:px-14 xl:px-20">
					<Link
						to="/"
						className="inline-flex w-fit items-center gap-2.5 text-lg font-semibold tracking-tight text-foreground transition-colors hover:text-primary"
					>
						<LogoIcon className="size-8" />
						<span>Clerical</span>
					</Link>

					<div className="flex flex-1 items-center justify-center py-10">
						<div className="w-full max-w-[22rem]">
							<div className="mb-8">
								<h1 className="text-[1.65rem] font-semibold leading-tight tracking-tight text-foreground">
									Welcome back
								</h1>
								<p className="mt-2.5 text-[0.9rem] leading-relaxed text-muted-foreground">
									Sign in to your firm workspace to continue.
								</p>
							</div>

							<FieldGroup>
								<Field>
									<Button
										type="button"
										variant="outline"
										className="h-11 w-full"
										onClick={handleGoogleSignIn}
										disabled={loading !== null}
									>
										<GoogleIcon />
										{loading === "google"
											? "Connecting..."
											: "Continue with Google"}
									</Button>
								</Field>

								<Field>
									<Button
										type="button"
										variant="outline"
										className="h-11 w-full"
										onClick={handleMicrosoftSignIn}
										disabled={loading !== null}
									>
										<MicrosoftIcon />
										{loading === "microsoft"
											? "Connecting..."
											: "Continue with Microsoft"}
									</Button>
								</Field>

								{error ? (
									<Field>
										<Alert variant="destructive">
											<AlertCircle />
											<AlertTitle>Sign-in error</AlertTitle>
											<AlertDescription>{error}</AlertDescription>
										</Alert>
									</Field>
								) : null}
							</FieldGroup>
						</div>
					</div>

					<p className="pb-2 text-center text-xs text-muted-foreground">
						By continuing, you agree to our{" "}
						<a
							href="/terms"
							className="underline underline-offset-2 hover:text-foreground"
						>
							Terms
						</a>{" "}
						and{" "}
						<a
							href="/privacy"
							className="underline underline-offset-2 hover:text-foreground"
						>
							Privacy Policy
						</a>
						.
					</p>
				</div>
			</div>

			<div
				className="auth-brand-panel relative hidden overflow-hidden lg:flex lg:flex-col"
				style={{ background: BRAND_BG }}
			>
				<div
					className="absolute -right-20 -top-20 size-[480px] rounded-full blur-[120px]"
					style={{ background: "oklch(0.55 0.13 55 / 0.12)" }}
				/>
				<div
					className="absolute -bottom-28 -left-12 size-[360px] rounded-full blur-[90px]"
					style={{ background: "oklch(0.48 0.11 52 / 0.10)" }}
				/>

				<div className="relative z-10 flex h-full flex-col p-14 xl:p-20">
					<div className="auth-stagger-1 max-w-lg">
						<h2 className="font-heading text-[3.25rem] leading-[1.06] tracking-tight text-cream xl:text-[3.75rem]">
							Your firm, <em className="text-copper-light">automated.</em>
						</h2>
						<p className="mt-6 max-w-[22rem] text-[0.95rem] leading-relaxed text-cream/40">
							From intake to filing, Clerical handles the admin so your team can
							focus on what matters.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}

export default function LoginPage() {
	return (
		<>
			<Seo
				title="Log In | Clerical"
				description="Secure login for Clerical firm workspaces."
				path={LOGIN_PATH}
				robots="noindex,nofollow"
			/>
			<CustomSignIn />
		</>
	);
}
